/* tslint:disable:jsx-no-lambda WI:47647 */
import * as React from 'react';
import { TextFieldWithCursorAtEnd } from './TextFieldWithCursorAtEnd';
import { ITextField, ITextFieldProps } from 'office-ui-fabric-react/lib/TextField';
import { KeyboardEvent } from 'react';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { observer } from 'mobx-react';

let styles = require('./EditableTitle.scss');
let classNames = require('classnames/bind').bind(styles);

let shouldUpdateIsEditingOnFocus = true;
export interface EditableTitleProps extends React.AllHTMLAttributes<HTMLElement> {
    /**
     * When isEditing is true, the component will autofocus
     * updating isEditing to false externally will cause the focus to be lost
     * */
    isEditing: boolean;
    title: string;
    onEnterEdit: () => void;
    onExitEdit: () => void;
    onSaveTitle: (title: string) => void;
    onTextFieldKeyDown?: (target: KeyboardEvent<EventTarget>) => void;
    moveFocusAway?: () => void;
    titleStyle?: string;
    textFieldProps?: ITextFieldProps;
    titleWrapperStyle?: string;
}

@observer
export default class EditableTitle extends React.Component<EditableTitleProps, {}> {
    private editTitleTextField: React.RefObject<ITextField> = React.createRef();
    private ignoreSaveOnBlur: boolean = false;

    render() {
        const {
            isEditing,
            title,
            placeholder,
            titleStyle,
            textFieldProps,
            titleWrapperStyle,
        } = this.props;

        return (
            <div className={classNames(styles.titleWrapper, titleWrapperStyle)}>
                {!isEditing && (
                    <span
                        data-is-focusable={true}
                        tabIndex={-1}
                        className={classNames(styles.displayTitle, titleStyle)}
                        /** setting isEditing onFocus allows us to enter edit mode when the component is navigated to.
                         * We do not want this behaivor onMouseDown so we set shouldUpdateIsEditingOnFocus to false. */
                        onFocus={() =>
                            shouldUpdateIsEditingOnFocus
                                ? this.props.onEnterEdit()
                                : (shouldUpdateIsEditingOnFocus = true)
                        }
                        onMouseDown={target => {
                            shouldUpdateIsEditingOnFocus = false;
                        }}>
                        {title ? title : ''}
                    </span>
                )}
                {isEditing && (
                    <TextFieldWithCursorAtEnd
                        ref={this.editTitleTextField}
                        className={classNames(styles.editableTitle, titleStyle)}
                        defaultValue={title}
                        placeholder={placeholder}
                        multiline={true}
                        autoFocus={true}
                        autoAdjustHeight={true}
                        resizable={false}
                        borderless={true}
                        underlined={false}
                        rows={1}
                        onKeyDown={this.onSubjectKeyDown}
                        aria-multiline={false}
                        onBlur={this.handleOnBlur}
                        {...textFieldProps}
                    />
                )}
            </div>
        );
    }

    public isTextSelected = (): boolean => {
        if (!this.props.isEditing) {
            return false;
        }
        const textField = this.editTitleTextField.current;
        return textField.selectionStart !== textField.selectionEnd;
    };

    public isCursorAtStart = (): boolean => {
        if (!this.props.isEditing) {
            return false;
        }
        const textField = this.editTitleTextField.current;
        const isCursorAtStart = textField.selectionStart === 0;
        return isCursorAtStart;
    };

    public isCursorAtEnd = (): boolean => {
        if (!this.props.isEditing) {
            return false;
        }
        const textField = this.editTitleTextField.current;
        const isCursorAtEnd = textField.selectionStart === textField.value.length;
        return isCursorAtEnd;
    };

    private handleOnBlur = () => {
        // Always exit edit mode
        this.props.onExitEdit();

        if (this.editTitleTextField && this.editTitleTextField.current) {
            !this.ignoreSaveOnBlur
                ? this.props.onSaveTitle(this.editTitleTextField.current.value)
                : (this.ignoreSaveOnBlur = false);
        }
    };

    private handleEnter = () => {
        this.ignoreSaveOnBlur = true;
        this.props.onSaveTitle(this.editTitleTextField.current.value);
        this.props.onExitEdit();
        this.props.moveFocusAway && this.props.moveFocusAway();
    };

    private handleEscape = () => {
        this.ignoreSaveOnBlur = true;
        // Don't save title
        this.props.onExitEdit();
        this.props.moveFocusAway && this.props.moveFocusAway();
    };

    private onSubjectKeyDown = (target: KeyboardEvent<EventTarget>) => {
        this.props.onTextFieldKeyDown && this.props.onTextFieldKeyDown(target);

        switch (target.keyCode) {
            case KeyCodes.escape:
                target.stopPropagation();
                target.preventDefault();
                this.handleEscape();
                break;
            case KeyCodes.enter:
                target.stopPropagation();
                target.preventDefault();
                this.handleEnter();
                break;
        }
    };
}
